import { useEffect, useState } from 'react';
import { getCompanies, getContactList, getContactTypes, getLanguageTypes } from 'api';
import getContacts from 'api/contacts/getContactsFromCustomer';
import { makeContactsFormat } from 'containers/pages/contacts/libs';
import { useNotificationProvider } from 'hooks';

// Normalize contacts from company filter API
const normalizeContact = (raw) => ({
  id: raw.contact_id ?? raw.id ?? String(raw.contact_id ?? raw.id ?? Math.random()),
  firstName: raw.first_name ?? '',
  lastName: raw.last_name ?? '',
  phoneWork: raw.phone_work ?? '',
  phoneMobile: raw.phone_cell ?? '',
  emailWork: raw.email_work || raw.email_home || '',
});

const normalizeContacts = (data) => (Array.isArray(data) ? data : []).map(normalizeContact);

/**
 * @function useContacts
 * @returns {ContactsState}
 */
function useContacts() {
	const { notifyError } = useNotificationProvider();
	const [isLoading, setIsLoading] = useState(false);
	const [isOpenForm, setIsOpenForm] = useState(false);
	const [isAddMode, setIsAddMode] = useState(true);
	const [contacts, setContacts] = useState([]);
	const [originalContacts, setOriginalContacts] = useState([]); // Keep full list for restoration
	const [searchedContacts, setSearchedContacts] = useState([]);
	const [estContacts, setEstContacts] = useState([]);
	const [companies, setCompanies] = useState([]);
	const [contactTypes, setContactTypes] = useState([]);
	const [languageTypes, setLanguageTypes] = useState([]);
	const [existingContactId, setExistingContactId] = useState(null);
	const [searchInput, setSearchInput] = useState('');
	const [selectedCompany, setSelectedCompany] = useState('');
	const [isCompanyFiltering, setIsCompanyFiltering] = useState(false);

	/**
	 * @function onForm
	 * @returns {void}
	 */
	const onForm = () => {
		if (isOpenForm) {
			setIsOpenForm(false);
			setEstContacts([]);
			setIsAddMode(true);
		} else {
			setIsOpenForm(true);
		}
	};

	/**
	 * @function onSearch
	 * @param {Contact[]} filteredContacts
	 * @returns {void}
	 */
	const onSearch = (filteredContacts) => {
		setSearchedContacts(filteredContacts);
	};

	/**
	 * @function onReset
	 * @returns {void}
	 */
	const onReset = () => {
		setSearchedContacts(contacts);
	};

	/**
	 * @function onEstCont
	 * @param {Contact[]} contacts
	 * @returns {void}
	 */
	const onEstCont = (contacts) => {
		setEstContacts(contacts);
	};

	/**
	 * @function onMode
	 * @param {boolean} mode
	 * @returns {void}
	 */
	const onMode = (mode) => {
		setIsAddMode(mode);
	};

	/**
	 * @function onGetExistingContactId
	 * @param {string} id
	 * @returns {void}
	 */
	const onGetExistingContactId = (id) => {
		setExistingContactId(id);
	};

	/**
	 * @function onSearchInput
	 * @param {string} input
	 * @returns {void}
	 */
	const onSearchInput = (input) => {
		setSearchInput(input);
	};

	/**
	 * @function onCompanyFilter
	 * @param {string} companyId
	 * @returns {Promise}
	 */
	const onCompanyFilter = async (companyId) => {
		try {
			setSelectedCompany(companyId);
			
			if (!companyId) {
				// Restore original contacts when filter is cleared
				setContacts(originalContacts);
				setSearchedContacts(originalContacts);
				onSearchInput(''); // Clear search
				return;
			}

			setIsCompanyFiltering(true);
			const data = await getContacts(companyId);
			const normalized = normalizeContacts(data);
			
			// Update contacts with filtered data
			setContacts(normalized);
			setSearchedContacts(normalized);
			onSearchInput(''); // Clear search when company filter changes
			
		} catch (error) {
			notifyError('Failed to load company contacts: ' + error.message);
			setContacts([]);
			setSearchedContacts([]);
		} finally {
			setIsCompanyFiltering(false);
		}
	};

	/**
	 * @function onContacts
	 * @returns {Promise}
	 */
	const onContacts = async () => {
		try {
			const _getContactList = await getContactList();
			const _contacts = await makeContactsFormat(_getContactList);
			
			// Update both current and original contacts
			setContacts(_contacts);
			setOriginalContacts(_contacts);
			setSearchedContacts(_contacts);
			onSearchInput('');
			
			// Clear company filter when refreshing contacts
			setSelectedCompany('');
		} catch (error) {
			notifyError(error.message);
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const _getContactList = await getContactList();
				const _contacts = await makeContactsFormat(_getContactList);
				const _companies = await getCompanies();
				const _contactTypes = await getContactTypes();
				const _languageTypes = await getLanguageTypes();
				
				setContacts(_contacts);
				setOriginalContacts(_contacts);
				setSearchedContacts(_contacts);
				setCompanies(_companies);
				setContactTypes(_contactTypes);
				setLanguageTypes(_languageTypes);
			} catch (error) {
				notifyError(error.message);
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	}, [notifyError]);

	const state = {
		existingContactId,
		isLoading: isLoading || isCompanyFiltering,
		companies,
		contactTypes,
		languageTypes,
		isAddMode,
		isOpenForm,
		contacts,
		searchedContacts,
		estContacts,
		searchInput,
		selectedCompany,
		isCompanyFiltering,
	};

	const handler = {
		onForm,
		onSearch,
		onReset,
		onEstCont,
		onMode,
		onGetExistingContactId,
		onContacts,
		onSearchInput,
		onCompanyFilter,
	};

	return { state, handler };
}

export default useContacts;